import { type ComponentType } from 'react'
import { useRecoilValue } from 'recoil'
import { FileDropzone as FileDropzoneJs } from 'components'
import { IMAGE_MIME_WILDCARD } from '@messenger/consts'
import { userAtom } from '@/atoms/_globalAtoms'
import { caseImageTilesSelector } from '@/atoms/caseImageAtoms'
import {
  useCaseImageVariants,
  useEnhanceImage,
  useSharedEnhance,
} from '@/hooks/caseImages'
import { CaseImageTile } from './CaseImageTile'
import { EnhanceModal } from './EnhanceModal'
import { EnhancePanel } from './EnhancePanel'
import * as S from './CaseImageTile.styled'
import { CASE_IMAGES_TEST_IDS } from './caseImagesTestIds'

/**
 * The upload dropzone is a JavaScript component, so TypeScript infers its
 * props from the defaults (`fileTypes = []` reads as `never[]`). This names
 * the props the section actually passes.
 */
type FileDropzoneProps = {
  height?: string
  margin?: string
  text?: string
  fileTypes?: string[]
  allowMultiple?: boolean
  onDrop?: (files: File[]) => void
  isLoading?: boolean
}
const FileDropzone =
  FileDropzoneJs as unknown as ComponentType<FileDropzoneProps>

/**
 * How the section shows the Enhance flow. The grid itself is the same in
 * both; only the container of the Enhance stages differs.
 *
 * - `takeover`: the Enhance panel replaces the upload dropzone. Used by the
 *   case details modal, where the rule is never to open a modal on top of
 *   that modal.
 * - `modal`: the dropzone stays and every Enhance stage opens in a modal
 *   centred over the page. Used by the editor's Case tab, whose ~270px
 *   sidebar is too narrow for the panel.
 */
export type CaseImagesPresentation = 'takeover' | 'modal'

type CaseImagesSectionProps = {
  /** The open case; an accepted enhancement reloads its images and variants. */
  caseId: string
  /** Where the Enhance flow is drawn; see `CaseImagesPresentation`. */
  presentation: CaseImagesPresentation
  /** Copy for the upload dropzone. */
  dropzoneText: string
  /** True while an upload batch is processing (dropzone shows its spinner). */
  imagesLoading: boolean
  onDrop: (files: File[]) => void
  /** Existing move-to-front behaviour; receives the grid index. */
  onSetDefault: (index: number) => void
  /** Existing delete behaviour; receives the grid index. */
  onDelete: (index: number) => void
  /** Reports request failures the way the surrounding tab already does. */
  onError: (message: string, error: Error) => void
}

/**
 * The "Images of the Deceased" section: the upload dropzone, the Enhance
 * flow in whichever container `presentation` asks for, and the grid of
 * photo squares. Both hosts (the case details modal and the editor's Case
 * tab) render this one component. Images, variants, and pricing are loaded
 * by whoever opens or refreshes the case
 * (`useCaseImageVariants().loadCaseImages`), not here.
 *
 * In the `modal` presentation the section drives the editor's shared Enhance
 * flow (`EditorEnhanceProvider`) when one is present, so the grid and the
 * canvas warnings open the same modal instance; the provider then renders
 * the modal. Without a provider, and always in the `takeover` presentation,
 * the section runs its own flow.
 */
export const CaseImagesSection = ({
  caseId,
  presentation,
  dropzoneText,
  imagesLoading,
  onDrop,
  onSetDefault,
  onDelete,
  onError,
}: CaseImagesSectionProps) => {
  const tiles = useRecoilValue(caseImageTilesSelector)
  const user = useRecoilValue(userAtom)
  const { loadCaseImagesWithVariants, loadCaseImageVariants } =
    useCaseImageVariants()
  const takeover = presentation === 'takeover'
  // The takeover host (the case details modal) can itself be open inside the
  // editor; it must never borrow the editor's flow or the modal would open
  // over it.
  const shared = useSharedEnhance()

  // After an accept, a revert or its undo in the section's own flow,
  // everything that shows the photo has to move to the file the server now
  // prefers. The server has already moved every saved layout page of the
  // case, in MDL and in m3, in the transaction that accepted or reverted.
  // Inside the editor the editor's complete refresh also moves what it holds
  // (images, template slots, canvas items, the pages it would save);
  // elsewhere the case's images and their variants are reloaded. The refresh
  // waits for the variants as well as the image list: the tile's file and
  // its Revert or Show enhancement item are read off the variants, so the
  // flow must not close before they answer.
  const refreshShownImage = async (caseImageId: string) => {
    if (shared) {
      await shared.refreshShownImage(caseImageId)
      return
    }
    await loadCaseImagesWithVariants(caseId)
  }

  const ownEnhance = useEnhanceImage({
    userId: user?.id,
    onShownImageChanged: refreshShownImage,
    // A finished generation: reload the variants so the tile holds it.
    onVariantsChanged: () => loadCaseImageVariants(caseId),
    onError,
  })
  const sharedEnhance = !takeover && shared ? shared.enhance : null
  const enhance = sharedEnhance ?? ownEnhance

  // The takeover hides the dropzone while a stage is active; the modal
  // leaves the sidebar as it is and floats above it.
  const showDropzone = !takeover || enhance.state.stage === 'idle'

  return (
    <>
      {showDropzone && (
        <FileDropzone
          height="200px"
          margin="0 0 24px 0"
          text={dropzoneText}
          fileTypes={[IMAGE_MIME_WILDCARD]}
          allowMultiple={true}
          onDrop={onDrop}
          isLoading={imagesLoading}
        />
      )}
      {takeover ? (
        <EnhancePanel enhance={enhance} layout="takeover" />
      ) : (
        // The provider renders the shared modal; a second one here would
        // draw every stage twice.
        !sharedEnhance && <EnhanceModal enhance={enhance} />
      )}
      <S.Grid data-testid={CASE_IMAGES_TEST_IDS.GRID}>
        {tiles.map((tile) => (
          <CaseImageTile
            key={tile.image.id}
            tile={tile}
            isDefault={tile.index === 0}
            enhancing={enhance.enhancingImageId === tile.image.id}
            enhanceDisabled={enhance.enhanceLocked}
            onSetDefault={onSetDefault}
            onEnhance={enhance.requestEnhance}
            onDelete={onDelete}
            onCompare={enhance.openCompare}
            onRevert={enhance.openRevert}
            onShowEnhancement={(tile) => void enhance.showEnhancement(tile)}
          />
        ))}
      </S.Grid>
    </>
  )
}
